import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Circle } from "lucide-react";
import { gql, GraphQLError } from "../lib/api";
import { CHANGE_PASSWORD_MUTATION } from "../lib/operations";
import { useAuth } from "../context/AuthContext";
import { ErrorMessage } from "../components/ErrorMessage";
import { COMPLEXITY_RULES, firstUnmetRule } from "../lib/passwordPolicy";
import type { PasswordChangeResult } from "../lib/types";

export function ChangePassword() {
  const { token, decoded, initializing, setToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (initializing) return null;
  if (!token || !decoded) {
    return <Navigate to="/signin" replace />;
  }

  const localError = newPassword ? firstUnmetRule(newPassword) : null;
  // Preserve location state from -> so a forced-rotation redirect lands
  // back at the original destination after the password is updated.
  // Parity with SignIn's behavior.
  const redirectTo =
    (location.state as { from?: { pathname?: string } } | null)?.from
      ?.pathname ?? "/applications";

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError("");

    if (localError) {
      setError(localError);
      return;
    }
    if (newPassword !== confirm) {
      setError("Passwords do not match.");
      return;
    }
    if (newPassword === oldPassword) {
      setError("New password must differ from the current one.");
      return;
    }

    setLoading(true);
    try {
      const data = await gql<{ changePassword: PasswordChangeResult }>(
        CHANGE_PASSWORD_MUTATION,
        { oldPassword, newPassword },
      );
      const result = data.changePassword;
      if (result.status !== "success" || !result.jwt) {
        setError(result.message ?? "Password change failed.");
        return;
      }
      setToken(result.jwt);
      navigate(redirectTo, { replace: true });
    } catch (err: unknown) {
      if (err instanceof GraphQLError) {
        setError(err.message);
      } else {
        setError(
          err instanceof Error
            ? err.message
            : "Password change failed. Please try again.",
        );
      }
    } finally {
      setLoading(false);
    }
  }

  const isForcedRotation = decoded.scope === "password_change";

  return (
    <div className="flex min-h-screen items-center justify-center bg-brand-bg p-4">
      <div className="auth-shell">
        <div className="auth-panel">
          <div className="auth-panel-header px-8 py-6 text-center">
            <h1 className="brand-logo text-2xl">Payment Products</h1>
            <p className="mt-1 text-sm text-brand-muted">
              {isForcedRotation
                ? "Set a new password to continue"
                : "Change your password"}
            </p>
          </div>
          <form onSubmit={handleSubmit} className="space-y-4 p-8">
            {error && <ErrorMessage message={error} />}
            <div>
              <label htmlFor="cp-current" className="label mb-1 block">
                {isForcedRotation ? "Temporary password" : "Current password"}
              </label>
              <input
                id="cp-current"
                name="current-password"
                type="password"
                autoComplete="current-password"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                className="input"
                required
                autoFocus
                maxLength={128}
              />
            </div>
            <div>
              <label htmlFor="cp-new" className="label mb-1 block">
                New password
              </label>
              <input
                id="cp-new"
                name="new-password"
                type="password"
                autoComplete="new-password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="input"
                required
                maxLength={128}
                aria-describedby="cp-rules"
              />
              <ul
                id="cp-rules"
                aria-live="polite"
                className="mt-2 space-y-1 text-xs"
              >
                {COMPLEXITY_RULES.map((rule) => {
                  const met = rule.test(newPassword);
                  return (
                    <li
                      key={rule.label}
                      className={`flex items-center gap-1.5 ${
                        met ? "text-emerald-300" : "text-brand-muted"
                      }`}
                    >
                      {met ? (
                        <CheckCircle2
                          className="h-3.5 w-3.5"
                          aria-hidden="true"
                        />
                      ) : (
                        <Circle className="h-3.5 w-3.5" aria-hidden="true" />
                      )}
                      <span>{rule.label}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
            <div>
              <label htmlFor="cp-confirm" className="label mb-1 block">
                Confirm new password
              </label>
              <input
                id="cp-confirm"
                name="confirm-password"
                type="password"
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                className="input"
                required
                maxLength={128}
              />
            </div>
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full py-2.5"
            >
              {loading ? "Updating..." : "Update password"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
